import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModuleAppShell from "../components/ModuleAppShell";
import { useModuleScope } from "../contexts/ModuleScopeContext";
import { useAuth } from "../contexts/AuthContext";
import { ROLE_LABELS } from "../lib/rbac";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import AIPanel from "../components/AIPanel";
import EmptyState from "../components/EmptyState";
import { AlertTriangle, ClipboardCheck, UserPlus, DollarSign, CalendarPlus, Banknote, Clock } from "lucide-react";
import {
    WelcomeTile, WeeklyBarsTile, PayrollRingTile,
    ProjectedCostTile, ProjectedCostModal, WeeklyCostModal,
    AlertsMiniTile, RecentActivityTile, TopRowsTile, QuickAddTile, AlertRow,
    fmtDateShort, fmtTodayLong, todayIso,
} from "../components/BentoTiles";
import IncidentsKpiTile from "../components/IncidentsKpiTile";

export default function ModuleDashboard() {
    return <ModuleAppShell title="Module Dashboard"><Body /></ModuleAppShell>;
}

function Body() {
    const scope = useModuleScope();
    const { user } = useAuth();
    const navigate = useNavigate();
    // Iter60.p50 — Attendance Officer never sees the Module
    // Dashboard tiles. Silently forward them to the Attendance page
    // for their scope so bookmarks / back-button just work.
    useEffect(() => {
        if (user?.role !== "asl_attendance_officer") return;
        if (!scope) return;
        navigate(`/m/${scope.moduleCode.toLowerCase()}/attendance`, { replace: true });
    }, [user?.role, scope, navigate]);
    const [data, setData] = useState(null);
    const [legacy, setLegacy] = useState(null);
    const [period, setPeriod] = useState(null);
    const [loading, setLoading] = useState(true);
    const [breakdownOpen, setBreakdownOpen] = useState(false);
    const [contributors, setContributors] = useState([]);
    // Iter60.p31 — Weekly Cost read-only modal state
    const [weeklyOpen, setWeeklyOpen] = useState(false);
    const [weeklyData, setWeeklyData] = useState(null);
    const [weeklyLoading, setWeeklyLoading] = useState(false);
    const today = todayIso();
    const base = scope ? `/m/${scope.moduleCode.toLowerCase()}` : "";

    useEffect(() => {
        if (!scope) return;
        setLoading(true);
        Promise.all([
            api.get(`/dashboard/module/${scope.moduleCode.toLowerCase()}`),
            api.get(`/dashboard/overview?module_id=${scope.moduleId}`),
        ])
            .then(([d1, d2]) => {
                // Iter60.p31 followup — Weekly Cost KPI card was
                // reading `kpis.week_cost.value` which comes from a
                // payroll-period totals sum (draft/prepared periods
                // hold `totals={}` → $0). The backend now embeds
                // `weekly_cost_total` (actual attended cost, same math
                // as the read-only modal). Hoist it into the kpis
                // object so the shared WelcomeTile picks it up
                // without needing a bespoke prop.
                const merged = d1.data || {};
                if (
                    merged.weekly_cost_total !== undefined
                    && merged.weekly_cost_total !== null
                    && merged.kpis
                ) {
                    merged.kpis = {
                        ...merged.kpis,
                        week_cost: {
                            ...(merged.kpis.week_cost || {}),
                            value: merged.weekly_cost_total,
                        },
                    };
                }
                setData(merged);
                setLegacy(d2.data);
                // Iter60.p29 — Current Payroll Period now comes from the
                // module dashboard endpoint (server-side, module-scoped).
                // Replaces a `/payroll/periods?module_code=…` fetch that
                // was silently unfiltered (the endpoint only knows
                // `module_id`) and leaked AP's newest period onto IC.
                setPeriod(merged?.current_payroll_period || null);
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [scope]);

    if (!scope) return null;

    const kpis = data?.kpis;
    const trend = data?.attendance_trend || [];
    const last7 = trend.slice(-7);
    const cbp = data?.cost_by_project || [];
    const exceptions = data?.recent_exceptions || [];
    const actions = data?.recent_actions || [];

    /* Iter60.p30 (Task C) — Projected Cost replaces the previous
       "Cost trend · 30 days" tile. Legacy `cost_trend` field is still
       returned by the backend and consumed by master pages, but the
       module dashboard now uses `projected_cost` (current open period
       actual + projection breakdown). */
    const projected = data?.projected_cost || null;

    /* Iter59.p20 — Welcome tile now surfaces the user's identity so the
       page reads as "Welcome back <name>" instead of just showing the module
       short code. Falls back to role label, then to a generic greeting. */
    const welcomeHeading = user?.full_name || user?.name
        || (user?.role ? (ROLE_LABELS[user.role] || user.role) : "Welcome back");

    /* Alerts summary */
    const openExceptions = kpis?.exceptions_open?.value || 0;
    const overduePayroll = legacy?.payroll?.periods_awaiting_funding || 0;
    const missingRates = 0; // Placeholder — no dedicated endpoint yet.
    const alertsCount = openExceptions + overduePayroll + missingRates;

    /* Top-projects rows normalized for the shared TopRowsTile shape */
    const topProjectRows = cbp.slice(0, 4).map((p) => ({
        id: p.project_id,
        label: p.project_name,
        cost: p.cost,
    }));

    return (
        <section className="asl-bento" data-testid="module-dashboard">
            <div className="asl-bento-grid">
                <WelcomeTile
                    heading={welcomeHeading}
                    subheading={`${fmtTodayLong()} · ${scope.module.name}`}
                    kpis={kpis}
                    loading={loading}
                    onKpiClick={{
                        active: () => navigate(`${base}/staff?status=active`),
                        present: () => navigate(`${base}/attendance?date=${today}&status=present`),
                        // Iter60.p31 — Weekly Cost opens a READ-ONLY
                        // breakdown modal (per-day + top contributors).
                        // Was navigating to the period detail page,
                        // which exposed Prepare/Recompute affordances.
                        weekly: async () => {
                            const modId = data?.scope?.module?.id;
                            if (!modId) return;
                            setWeeklyOpen(true);
                            setWeeklyLoading(true);
                            try {
                                const { data: wc } = await api.get(
                                    `/dashboard/module/${modId}/weekly-cost`
                                );
                                setWeeklyData(wc);
                            } catch (e) {
                                toast.error(formatApiError(e));
                                setWeeklyData(null);
                            } finally {
                                setWeeklyLoading(false);
                            }
                        },
                        exceptions: () => navigate(`${base}/attendance/exceptions`),
                    }}
                />
                <WeeklyBarsTile last7={last7} loading={loading}
                    onClick={() => navigate(`${base}/attendance`)} />
                <PayrollRingTile period={period} loading={loading}
                    onClick={() => period && navigate(`${base}/payroll/${period.id}`)} />
                <ProjectedCostTile
                    data={projected}
                    loading={loading}
                    moduleId={data?.scope?.module?.id}
                    onShowBreakdown={async () => {
                        if (!projected?.period_id) return;
                        setBreakdownOpen(true);
                        try {
                            // Best-effort top-contributors fetch. Endpoint
                            // returns the payroll lines already summed by
                            // employee (net_payable). Falls back silently.
                            const { data: lines } = await api.get(`/payroll/periods/${projected.period_id}/lines`);
                            const arr = (lines?.items || lines || [])
                                .map((l) => ({
                                    employee_id: l.employee_id,
                                    name: l.employee_name,
                                    employee_code: l.employee_code,
                                    amount: (l.totals && l.totals.net_payable) || l.net_payable || 0,
                                }))
                                .sort((a, b) => (b.amount || 0) - (a.amount || 0))
                                .slice(0, 10);
                            setContributors(arr);
                        } catch { setContributors([]); }
                    }}
                />
                <AlertsMiniTile
                    count={alertsCount}
                    breakdown={[
                        { label: "Open exceptions", value: openExceptions, link: `${base}/attendance/exceptions` },
                        { label: "Overdue payroll", value: overduePayroll, link: `${base}/payroll?status=approved` },
                        { label: "Missing wage rates", value: missingRates, link: `${base}/wage-rates` },
                    ]}
                    loading={loading}
                    onNav={(l) => navigate(l)}
                />
                <RecentActivityTile items={actions} loading={loading} />
                <TopRowsTile
                    title="Top projects this month"
                    rows={topProjectRows}
                    loading={loading}
                    emptyTitle="No projects yet"
                    emptyDescription="Assign attendance to projects to populate."
                />
                <QuickAddTile
                    actions={[
                        { icon: ClipboardCheck, label: "Attendance", to: `${base}/attendance` },
                        { icon: UserPlus, label: "Employee", to: `${base}/staff` },
                        { icon: DollarSign, label: "Wage Rate", to: `${base}/wage-rates` },
                        { icon: CalendarPlus, label: "Payroll", to: `${base}/payroll` },
                    ]}
                    onNav={(t) => navigate(t)}
                />
            </div>

            {/* Iter60.p55b — module-scoped Incidents & Emergencies tile.
                Full-width row below the main bento so Manager
                dashboards get the same "safety pulse" surface HR has
                on Home. */}
            {scope && (
                <IncidentsKpiTile
                    moduleCode={scope.moduleCode}
                    style={{ marginTop: 16 }}
                />
            )}

            {legacy?.alerts?.length > 0 && (
                <div className="asl-bento-alerts" data-testid="md-alerts">
                    {legacy.alerts.map((a, i) => (
                        <AlertRow key={i} alert={a} onClick={() => a.link && navigate(scopeLink(a.link, base))} />
                    ))}
                </div>
            )}

            <details className="asl-bento-ai" data-testid="md-ai-collapsible">
                <summary className="asl-bento-ai__summary">
                    <span className="text-label" style={{ color: "var(--asl-crimson-2)" }}>ASSISTANT</span>
                    <span className="ml-2 text-sm asl-navy-text font-semibold">Ask about this module</span>
                </summary>
                <div className="asl-bento-ai__body">
                    <AIPanel moduleId={scope.moduleId} moduleCode={scope.moduleCode} />
                </div>
            </details>

            {!loading && exceptions.length > 0 && (
                <div className="asl-card p-0 overflow-hidden" data-testid="md-recent-exceptions">
                    <div className="px-5 pt-5 pb-3 asl-section-title flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" style={{ color: "var(--asl-crimson-2)" }} />
                        Recent exceptions
                    </div>
                    {exceptions.map((e) => (
                        <button
                            key={e.id}
                            onClick={() => navigate(`${base}/attendance/exceptions`)}
                            className="w-full text-left px-4 py-3 transition"
                            style={{ borderTop: "1px solid var(--asl-divider)" }}
                            onMouseEnter={(ev) => (ev.currentTarget.style.background = "var(--asl-bg-alt)")}
                            onMouseLeave={(ev) => (ev.currentTarget.style.background = "transparent")}
                            data-testid={`md-exception-${e.id}`}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-body-lg truncate">
                                        {e.employee_name}
                                        {e.module_employee_id && (
                                            <span className="mono text-xs ml-2" style={{ color: "var(--asl-text-3)" }}>{e.module_employee_id}</span>
                                        )}
                                    </div>
                                    <div className="text-xs mt-0.5 truncate" style={{ color: "var(--asl-text-2)" }}>{e.reason}</div>
                                </div>
                                <div className="text-xs whitespace-nowrap" style={{ color: "var(--asl-text-3)" }}>{fmtDateShort(e.work_date || e.created_at)}</div>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            <ProjectedCostModal
                open={breakdownOpen}
                onClose={() => setBreakdownOpen(false)}
                data={projected}
                contributors={contributors}
            />

            <WeeklyCostModal
                open={weeklyOpen}
                onClose={() => setWeeklyOpen(false)}
                data={weeklyData}
                loading={weeklyLoading}
            />
        </section>
    );
}

function scopeLink(link, base) {
    if (!link) return base;
    if (link === "/contracts") return `${base}/contracts`;
    if (link === "/attendance/exceptions") return `${base}/attendance/exceptions`;
    if (link === "/payroll") return `${base}/payroll`;
    return link;
}
